/**
 * Home's Dashboard: full width, under the greeting header. One joined row of
 * type tiles (Deadlines, Expirations, Agent Activity, Intakes and Requests,
 * Tomorrow), the runway, then a widget per type in three columns. Every tile
 * and widget header opens its list.
 */
import dayjs from "dayjs";
import { ArrowUpRight, ChevronRight, Flag } from "lucide-react";
import { useMemo, type FC, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { buttonVariants, Button } from "@/components/ui/button";
import { initials } from "@/components/ui/inbox-row";
import { KINDS, type Kind } from "@/components/ui/kind-icon";
import { StatusBadge } from "@/components/ui/status-badge";
import { Tag } from "@/components/ui/tag";
import { Surface } from "@/components/surface";
import { useAgentsTrayStore } from "@/features/agentAttention/context/agentsTrayStore";
import type { CalendarEvent } from "@/features/calendar";
import {
  rangeForView,
  useCalendarEvents,
  useFirmReminders,
  useMergedCalendar
} from "@/features/calendar/queries";
import { CALENDAR_PATH, calendarPathFor } from "@/features/calendar/routes";
import { cn } from "@/lib/utils";

import type { HomeScope, RunwayDays } from "../dashboardLayoutStore";
import { humanizeStatus } from "../format";
import type { FilingDeadlineRow, IntakeRow } from "../types";
import { daysAway } from "./comingUp";
import { groupByKind, nextDated, runwayMarks } from "./homeKinds";
import { HomeRunway } from "./HomeRunway";
import { KindTile } from "./KindTile";
import { dueDay, rightLabel, type TriageItem } from "./triageModel";

export const WIDGET_ROWS = 5;

/** "7 AM", "2:30 PM": the Home cards' clock. */
const clock = (d: Date): string =>
  dayjs(d).format(dayjs(d).minute() === 0 ? "h A" : "h:mm A");

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

const PersonAvatar: FC<{ name: string }> = ({ name }) => (
  <Avatar size="xs" className="mt-px">
    <AvatarFallback>{initials(name) || "?"}</AvatarFallback>
  </Avatar>
);

/** A button styled as the row's action, inside a row that is the button. */
const RowAction: FC<{ children: ReactNode }> = ({ children }) => (
  <span
    aria-hidden
    className={cn(buttonVariants({ variant: "secondary", size: "xs" }))}
  >
    {children}
  </span>
);

const ROW =
  "flex w-full min-w-0 items-start gap-2.5 px-3 py-2.5 text-left outline-none hover:bg-fill-1 focus-visible:bg-fill-1";

const DashWidget: FC<{
  kind: Kind;
  title?: string;
  count?: number;
  action?: { label: string; onClick: () => void };
  children: ReactNode;
  "data-testid"?: string;
}> = ({ kind, title, count, action, children, ...rest }) => (
  <Surface data-testid={rest["data-testid"]}>
    <header className="flex h-10 shrink-0 items-center gap-2 border-b border-line-1 pr-1.5 pl-3">
      <KindTile kind={kind} />
      <span className="truncate text-base font-medium text-fg">
        {title ?? KINDS[kind].label}
      </span>
      {typeof count === "number" ? (
        <span className="text-sm text-fg-4 tabular-nums">{count}</span>
      ) : null}
      <span className="flex-1" />
      {action ? (
        <Button variant="ghost" size="xs" onClick={action.onClick}>
          {action.label}
          <ArrowUpRight className="text-fg-3" />
        </Button>
      ) : null}
    </header>
    <div className="flex flex-col divide-y divide-line-1">{children}</div>
  </Surface>
);

const Empty: FC<{ children: ReactNode }> = ({ children }) => (
  <p className="px-3 py-3 text-base text-fg-3">{children}</p>
);

const MoreRow: FC<{ count: number; label: string; onClick: () => void }> = ({
  count,
  label,
  onClick
}) => (
  <button
    type="button"
    onClick={onClick}
    className="flex h-8 items-center gap-1.5 px-3 text-left text-sm text-fg-3 outline-none hover:bg-fill-1 hover:text-fg focus-visible:bg-fill-1"
  >
    {count} more {label}
    <ChevronRight className="size-3 text-fg-4" />
  </button>
);

const visaOf = (t: TriageItem): string | null =>
  t.kind === "filing" ? (t.row as FilingDeadlineRow).visa_type : null;

/** Avatar, title (+ visa), "client · context", and the date with how far off. */
const DatedRow: FC<{
  item: TriageItem;
  now: Date;
  longDate?: boolean;
  onClick: () => void;
}> = ({ item, now, longDate = false, onClick }) => {
  const due = item.due ? dueDay(item.due) : null;
  const visa = visaOf(item);
  return (
    <button
      type="button"
      onClick={onClick}
      className={ROW}
      data-testid={`dash-row-${item.id}`}
    >
      <PersonAvatar name={item.clientName} />
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="truncate text-base text-fg">{item.title}</span>
          {visa ? <Tag>{visa}</Tag> : null}
        </span>
        <span className="truncate text-sm text-fg-3">
          {[item.clientName, visa ? null : item.caseLabel]
            .filter(Boolean)
            .join(" · ")}
        </span>
      </span>
      <span className="flex shrink-0 flex-col items-end gap-0.5">
        {due ? (
          <span className="text-base font-medium text-fg tabular-nums">
            {due.format(longDate ? "MMM D, YYYY" : "MMM D")}
          </span>
        ) : null}
        {item.overdue ? (
          <StatusBadge tone="danger">{rightLabel(item, now)}</StatusBadge>
        ) : due ? (
          <span className="text-sm text-fg-3">
            {daysAway(due.toDate(), now)}
          </span>
        ) : null}
      </span>
    </button>
  );
};

/** Avatar, title, "client · context" and the time; agent and reminder rows. */
const ItemRow: FC<{ item: TriageItem; now: Date; onClick: () => void }> = ({
  item,
  now,
  onClick
}) => (
  <button
    type="button"
    onClick={onClick}
    className={ROW}
    data-testid={`dash-row-${item.id}`}
  >
    <PersonAvatar name={item.clientName} />
    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
      <span className="truncate text-base text-fg">{item.title}</span>
      <span className="truncate text-sm text-fg-3">
        {[item.clientName, item.caseLabel].filter(Boolean).join(" · ")}
      </span>
    </span>
    {item.overdue ? (
      <StatusBadge tone="danger">{rightLabel(item, now)}</StatusBadge>
    ) : (
      <span className="shrink-0 pt-0.5 text-sm text-fg-3 tabular-nums">
        {rightLabel(item, now)}
      </span>
    )}
  </button>
);

const IntakeLine: FC<{
  title: string;
  client: string;
  sub: string;
  when: string;
  action: string;
  onClick: () => void;
  testId: string;
}> = ({ title, client, sub, when, action, onClick, testId }) => (
  <button type="button" onClick={onClick} className={ROW} data-testid={testId}>
    <PersonAvatar name={client} />
    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
      <span className="truncate text-base text-fg">{title}</span>
      <span className="truncate text-sm text-fg-3">
        {client} · {sub}
      </span>
      {when ? <span className="truncate text-sm text-fg-4">{when}</span> : null}
    </span>
    <RowAction>{action}</RowAction>
  </button>
);

const sentLabel = (iso: string | null, now: Date): string => {
  if (!iso) return "";
  const at = dayjs(iso);
  if (!at.isValid()) return "";
  const minutes = dayjs(now).diff(at, "minute");
  if (minutes < 60) return `${Math.max(1, minutes)}m ago`;
  const hours = dayjs(now).diff(at, "hour");
  if (hours < 24) return `${hours}h ago`;
  return at.format("MMM D");
};

/** Tomorrow's calendar for the scope: reminders plus synced events. */
const useTomorrow = (
  now: Date,
  scope: HomeScope,
  uid: string | null
): CalendarEvent[] => {
  const tomorrow = useMemo(() => dayjs(now).add(1, "day").toDate(), [now]);
  const range = useMemo(() => rangeForView("day", tomorrow), [tomorrow]);
  const reminders = useFirmReminders(range, {
    assignee: scope === "mine" ? uid : null
  });
  const synced = useCalendarEvents(range);
  const external = useMemo(
    () =>
      scope === "mine"
        ? synced.events.filter((e) => e.ownerUid === uid)
        : synced.events,
    [synced.events, scope, uid]
  );
  const events = useMergedCalendar(reminders.events, external);
  return useMemo(
    () =>
      events
        .filter((e) => dayjs(e.start).isSame(tomorrow, "day") || e.allDay)
        .sort(
          (a, b) =>
            Number(b.allDay) - Number(a.allDay) ||
            a.start.getTime() - b.start.getTime()
        ),
    [events, tomorrow]
  );
};

type Tile = {
  kind: Kind;
  label: string;
  value: number;
  sub: string;
  late?: boolean;
  onClick: () => void;
};

type Props = {
  /** The For Review items in the chosen scope. */
  items: TriageItem[];
  /** Every dated item in the scope, for the runway and "next" lines. */
  dated: TriageItem[];
  /** Intakes out with clients (sent, in progress, changes requested). */
  intakesInFlight: IntakeRow[];
  scope: HomeScope;
  uid: string | null;
  runwayDays: RunwayDays;
  onRunwayDaysChange: (days: RunwayDays) => void;
  onOpen: (id: string) => void;
  /** Open By Type at this group. */
  onOpenKind: (kind: Kind) => void;
  header?: ReactNode;
  now?: Date;
};

export const DashboardView: FC<Props> = ({
  items,
  dated,
  intakesInFlight,
  scope,
  uid,
  runwayDays,
  onRunwayDaysChange,
  onOpen,
  onOpenKind,
  header,
  now = new Date()
}) => {
  const navigate = useNavigate();
  const openAgentsTray = useAgentsTrayStore((s) => s.setOpen);
  const groups = useMemo(
    () => Object.fromEntries(groupByKind(items).map((g) => [g.kind, g.rows])),
    [items]
  ) as Record<Kind, TriageItem[]>;
  const datedByKind = useMemo(
    () => Object.fromEntries(groupByKind(dated).map((g) => [g.kind, g.rows])),
    [dated]
  ) as Record<Kind, TriageItem[]>;
  const marks = useMemo(() => runwayMarks(dated), [dated]);
  const tomorrow = useTomorrow(now, scope, uid);
  const tomorrowDate = dayjs(now).add(1, "day");
  const ids = useMemo(() => new Set(items.map((t) => t.id)), [items]);

  const open = (t: Pick<TriageItem, "id" | "path">) =>
    ids.has(t.id) ? onOpen(t.id) : navigate(t.path);
  const openTomorrow = () =>
    navigate(calendarPathFor(tomorrowDate.toDate(), "day"));

  const signals = groups.agent.filter((t) => t.kind === "approval");
  const approvals = signals.filter(
    (t) => (t.signal ?? "approval") === "approval"
  ).length;
  const updates = signals.length - approvals;
  const mentions = groups.agent.length - signals.length;
  const reviews = groups.intake.filter((t) => t.kind === "intake");
  const agentSummary = [
    approvals ? `${plural(approvals, "approval")} waiting on you` : null,
    updates ? plural(updates, "agent update") : null,
    mentions ? plural(mentions, "mention") : null
  ]
    .filter(Boolean)
    .join(" · ");

  const nextLine = (kind: Kind, none: string): string => {
    const next = nextDated(datedByKind[kind], now);
    return next ? `Next ${dueDay(next.due).format("MMM D")}` : none;
  };
  const deadlinesLate = groups.deadline.filter((t) => t.overdue).length;
  const expiredCount = groups.expiration.filter((t) => t.overdue).length;
  const firstTimed = tomorrow.find((e) => !e.allDay);

  const tiles: Tile[] = [
    {
      kind: "deadline",
      label: KINDS.deadline.label,
      value: groups.deadline.length,
      sub: deadlinesLate
        ? `${deadlinesLate} late`
        : nextLine("deadline", "No filing deadlines due"),
      late: deadlinesLate > 0,
      onClick: () => onOpenKind("deadline")
    },
    {
      kind: "expiration",
      label: KINDS.expiration.label,
      value: groups.expiration.length,
      sub: expiredCount
        ? `${expiredCount} already passed`
        : nextLine("expiration", "None in the next 30 days"),
      late: expiredCount > 0,
      onClick: () => onOpenKind("expiration")
    },
    {
      kind: "agent",
      label: KINDS.agent.label,
      value: groups.agent.length,
      sub: agentSummary || "Nothing waiting on you",
      onClick: () => onOpenKind("agent")
    },
    {
      kind: "intake",
      label: KINDS.intake.label,
      value: reviews.length + intakesInFlight.length,
      sub: `${reviews.length} to review${
        intakesInFlight.length ? ` · ${intakesInFlight.length} with client` : ""
      }`,
      onClick: () => onOpenKind("intake")
    },
    {
      kind: "calendar",
      label: "Tomorrow",
      value: tomorrow.length,
      sub: firstTimed
        ? `Tomorrow, first at ${clock(firstTimed.start)}`
        : tomorrow.length
        ? "All-day only"
        : "Nothing on the calendar",
      onClick: openTomorrow
    }
  ];

  const notes: Partial<Record<Kind, ReactNode>> = agentSummary
    ? { agent: `Not dated · ${agentSummary} now` }
    : {};

  const limited = (rows: TriageItem[]) => rows.slice(0, WIDGET_ROWS);
  const more = (rows: TriageItem[], kind: Kind, label: string) =>
    rows.length > WIDGET_ROWS ? (
      <MoreRow
        count={rows.length - WIDGET_ROWS}
        label={label}
        onClick={() => onOpenKind(kind)}
      />
    ) : null;

  return (
    <div
      className="flex min-h-0 flex-1 flex-col overflow-y-auto"
      data-testid="home-dashboard"
    >
      {header}
      <div className="flex flex-col gap-4 px-6 pt-4 pb-6">
        <Surface className="grid grid-cols-1 divide-y divide-line-1 sm:grid-cols-5 sm:divide-x sm:divide-y-0">
          {tiles.map((tile) => (
            <button
              key={tile.kind}
              type="button"
              onClick={tile.onClick}
              className="flex min-w-0 flex-col px-4 pt-3 pb-3.5 text-left outline-none hover:bg-fill-1 focus-visible:bg-fill-1"
              data-testid={`home-tile-${tile.kind}`}
            >
              <span className="flex min-w-0 items-center gap-2">
                <KindTile kind={tile.kind} />
                <span className="truncate text-sm text-fg-2">{tile.label}</span>
              </span>
              <span className="mt-2.5 text-2xl font-medium text-fg tabular-nums">
                {tile.value}
              </span>
              <span
                className={cn(
                  "truncate text-sm",
                  tile.late ? "text-danger" : "text-fg-3"
                )}
              >
                {tile.sub}
              </span>
            </button>
          ))}
        </Surface>

        <HomeRunway
          marks={marks}
          lanes={["deadline", "expiration", "intake", "calendar", "agent"]}
          notes={notes}
          days={runwayDays}
          onDaysChange={onRunwayDaysChange}
          onOpen={open}
          today={now}
        />

        <div className="grid items-start gap-4 lg:grid-cols-3">
          <DashWidget
            kind="agent"
            count={groups.agent.length}
            action={{
              label: "Open Agents",
              onClick: () => openAgentsTray(true)
            }}
            data-testid="home-widget-agent"
          >
            {groups.agent.length === 0 ? (
              <Empty>Nothing is waiting on you.</Empty>
            ) : (
              limited(groups.agent).map((t) => (
                <ItemRow
                  key={t.id}
                  item={t}
                  now={now}
                  onClick={() => onOpen(t.id)}
                />
              ))
            )}
            {more(groups.agent, "agent", "items")}
          </DashWidget>

          <div className="flex min-w-0 flex-col gap-4">
            <DashWidget
              kind="expiration"
              count={groups.expiration.length}
              action={{
                label: "Open Calendar",
                onClick: () => navigate(CALENDAR_PATH)
              }}
              data-testid="home-widget-expiration"
            >
              {groups.expiration.length === 0 ? (
                <Empty>No expirations in the next 30 days.</Empty>
              ) : (
                limited(groups.expiration).map((t) => (
                  <DatedRow
                    key={t.id}
                    item={t}
                    now={now}
                    longDate
                    onClick={() => onOpen(t.id)}
                  />
                ))
              )}
              {more(groups.expiration, "expiration", "expirations")}
            </DashWidget>

            <DashWidget
              kind="deadline"
              count={groups.deadline.length}
              action={{
                label: "Open Cases",
                onClick: () => navigate("/cases")
              }}
              data-testid="home-widget-deadline"
            >
              {groups.deadline.length === 0 ? (
                <div className="flex items-start gap-2.5 px-3 py-2.5">
                  <Flag className="mt-0.5 size-4 shrink-0 text-fg-4" />
                  <span className="flex min-w-0 flex-col gap-0.5">
                    <span className="text-base text-fg">
                      No Filing Deadlines Due
                    </span>
                    <span className="text-sm text-fg-3">
                      Nothing overdue or due in the next 7 days.
                    </span>
                  </span>
                </div>
              ) : (
                limited(groups.deadline).map((t) => (
                  <DatedRow
                    key={t.id}
                    item={t}
                    now={now}
                    onClick={() => onOpen(t.id)}
                  />
                ))
              )}
              {more(groups.deadline, "deadline", "deadlines")}
            </DashWidget>
          </div>

          <div className="flex min-w-0 flex-col gap-4">
            <DashWidget
              kind="intake"
              count={reviews.length + intakesInFlight.length}
              data-testid="home-widget-intake"
            >
              {reviews.length + intakesInFlight.length === 0 ? (
                <Empty>No intakes waiting or out with clients.</Empty>
              ) : null}
              {limited(reviews).map((t) => (
                <IntakeLine
                  key={t.id}
                  title={t.title}
                  client={t.clientName}
                  sub="Waiting for your review"
                  when={
                    t.changedAt
                      ? `Submitted ${sentLabel(t.changedAt, now)}`
                      : ""
                  }
                  action="Review"
                  onClick={() => onOpen(t.id)}
                  testId={`dash-row-${t.id}`}
                />
              ))}
              {intakesInFlight
                .slice(0, Math.max(0, WIDGET_ROWS - reviews.length))
                .map((i) => (
                  <IntakeLine
                    key={i.outgoing_intake_id}
                    title={i.title || "Intake"}
                    client={i.client || "Client"}
                    sub={`${humanizeStatus(i.status)} · with client`}
                    when={i.sent_at ? `Sent ${sentLabel(i.sent_at, now)}` : ""}
                    action="Open"
                    onClick={() => navigate(i.path)}
                    testId={`dash-row-flight:${i.outgoing_intake_id}`}
                  />
                ))}
              {more(reviews, "intake", "to review")}
            </DashWidget>

            <DashWidget
              kind="calendar"
              title={`Tomorrow, ${tomorrowDate.format("ddd MMM D")}`}
              count={tomorrow.length}
              action={{ label: "Open Calendar", onClick: openTomorrow }}
              data-testid="home-widget-tomorrow"
            >
              {tomorrow.length === 0 ? (
                <Empty>Nothing on the calendar tomorrow.</Empty>
              ) : (
                tomorrow.slice(0, 8).map((e) => (
                  <button
                    key={e.id}
                    type="button"
                    onClick={() => navigate(calendarPathFor(e.start, "day"))}
                    className="flex h-9 min-w-0 items-center gap-2.5 px-3 text-left outline-none hover:bg-fill-1 focus-visible:bg-fill-1"
                  >
                    <span className="w-14 shrink-0 text-sm text-fg-3 tabular-nums">
                      {e.allDay ? "All Day" : clock(e.start)}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-base text-fg">
                      {e.title}
                    </span>
                    {e.clientName ? (
                      <Avatar size="2xs">
                        <AvatarFallback>
                          {initials(e.clientName)}
                        </AvatarFallback>
                      </Avatar>
                    ) : null}
                  </button>
                ))
              )}
            </DashWidget>

            {groups.calendar.length ? (
              <DashWidget
                kind="calendar"
                title="Reminders"
                count={groups.calendar.length}
                action={{
                  label: "Open Calendar",
                  onClick: () => navigate(calendarPathFor(now, "day"))
                }}
                data-testid="home-widget-reminders"
              >
                {limited(groups.calendar).map((t) => (
                  <ItemRow
                    key={t.id}
                    item={t}
                    now={now}
                    onClick={() => onOpen(t.id)}
                  />
                ))}
                {more(groups.calendar, "calendar", "reminders")}
              </DashWidget>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};
